import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Tooltip,
  Divider,
  Checkbox,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import StoreIcon from '@mui/icons-material/Store';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import { theme } from '../../../theme';
import LocationStatusChip from './LocationStatusChip';

// --- Styled Components ---

const StyledCard = styled(Card)(({ theme: muiTheme }) => ({
  position: 'relative',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.borderRadius.xl,
  backgroundColor: '#fff',
  border: `1px solid ${theme.colors.border.light}`,
  overflow: 'visible', // Allow specific elements to pop out if needed, but usually hidden for cards
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.12)',
    borderColor: theme.colors.primary[200],
    '& .hover-arrow': {
      transform: 'translateX(4px)',
      opacity: 1,
    }
  },
}));

const CardImageContainer = styled(Box)({
  position: 'relative',
  height: 180,
  width: '100%',
  overflow: 'hidden',
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
});

const CardImage = styled(CardMedia)({
  height: '100%',
  width: '100%',
  objectFit: 'cover',
  transition: 'transform 0.5s ease',
  '.MuiCard-root:hover &': {
    transform: 'scale(1.05)',
  },
});

const GradientOverlay = styled(Box)(({ theme: muiTheme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `linear-gradient(to bottom, rgba(0,0,0,0) 60%, rgba(0,0,0,0.6) 100%)`,
  zIndex: 1,
}));

// Floating action button on the image
const ViewButton = styled(Box)(({ theme: muiTheme }) => ({
  position: 'absolute',
  bottom: 12,
  right: 12,
  zIndex: 2,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(8px)',
  borderRadius: '50%',
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  color: theme.colors.primary[600],
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.colors.primary[500],
    color: '#fff',
    transform: 'scale(1.1)',
  }
}));

const InfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 6,
  color: theme.colors.text.secondary,
});

const MetricBox = styled(Box)(({ theme: muiTheme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 12px',
  borderRadius: theme.borderRadius.lg,
  backgroundColor: theme.colors.background.secondary,
  border: `1px solid ${theme.colors.border.light}`,
  minWidth: 80,
}));

const SalonCard = ({
  salon,
  onClick,
  onDelete,
  selected = false,
  onSelect,
}) => {
  const [imgError, setImgError] = useState(false);

  // Derive display data
  const name = salon.name || 'Salon sans nom';
  const ownerName = salon.owner 
    ? `${salon.owner.first_name || ''} ${salon.owner.last_name || ''}`.trim() || 'Inconnu'
    : 'Inconnu';
  
  const employeesCount = salon.employees_count ?? 0;
  const appointmentsCount = salon.appointments_count ?? 0;
  const activeRepairStatus = ['queued', 'running'].includes(
    salon.latest_location_repair?.status,
  )
    ? salon.latest_location_repair.status
    : null;
  
  // Image handling
  // Prioritize primary_photo from the new serializer field, then fallback to old logic
  let imageUrl = salon.primary_photo;
  
  if (!imageUrl) {
    const primaryPhoto = salon.photos?.find(p => p.is_primary) || salon.photos?.[0];
    imageUrl = typeof primaryPhoto === 'string' ? primaryPhoto : primaryPhoto?.url;
  }
  
  // Fix localhost/media URLs if needed
  if (imageUrl && !imageUrl.startsWith('http')) {
     imageUrl = `http://localhost:8012${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`; 
  }

  const handleImgError = () => {
    setImgError(true);
  };

  return (
    <StyledCard onClick={onClick}>
      <CardImageContainer>
        {onSelect && (
          <Checkbox
            checked={selected}
            onClick={(event) => event.stopPropagation()}
            onChange={onSelect}
            inputProps={{ 'aria-label': `Sélectionner ${name}` }}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 4,
              bgcolor: 'rgba(255,255,255,0.92)',
              borderRadius: '50%',
              '&:hover': { bgcolor: 'white' },
            }}
          />
        )}
        {!imgError && imageUrl ? (
          <CardImage 
            component="img" 
            image={imageUrl} 
            alt={name} 
            onError={handleImgError}
          />
        ) : (
          // Fallback Gradient if no image
          <Box
            sx={{
              height: '100%',
              width: '100%',
              background: `linear-gradient(135deg, ${theme.colors.primary[100]} 0%, ${theme.colors.secondary[200]} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <StoreIcon sx={{ fontSize: 60, color: theme.colors.primary[300], opacity: 0.5 }} />
          </Box>
        )}
        <GradientOverlay />
        
        <Tooltip title="Voir les détails" placement="left">
          <ViewButton>
            <ArrowForwardIcon fontSize="small" />
          </ViewButton>
        </Tooltip>

        {onDelete && (
          <Tooltip title="Supprimer le salon" placement="left">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 3,
                bgcolor: 'rgba(255,255,255,0.92)',
                color: theme.colors.error[600],
                '&:hover': {
                  bgcolor: theme.colors.error[50],
                  color: theme.colors.error[700],
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </CardImageContainer>

      <CardContent sx={{ p: 2, pb: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header: Name and Owner */}
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 800, 
              color: theme.colors.text.primary,
              lineHeight: 1.2,
              mb: 0.5,
              fontSize: '1.1rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {name}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PersonOutlineIcon sx={{ fontSize: 16, color: theme.colors.primary[500] }} />
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontWeight: 500 }}>
              {ownerName}
            </Typography>
          </Box>
          <Box sx={{ mt: 1 }}>
            <LocationStatusChip status={activeRepairStatus || salon.location_status} />
            {salon.location_missing_fields?.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                Manque : {salon.location_missing_fields.join(', ')}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Address (Optional truncation) */}
        <InfoRow sx={{ mb: 2 }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 18, color: theme.colors.text.tertiary, flexShrink: 0 }} />
            <Typography 
              variant="caption" 
              sx={{ 
                color: theme.colors.text.secondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.3
              }}
            >
              {salon.formatted_address || 'Adresse non renseignée'}
            </Typography>
        </InfoRow>

        <Divider sx={{ my: 1.5, borderColor: theme.colors.border.light }} />

        {/* Footer Metrics */}
        <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
          <MetricBox sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: theme.colors.text.tertiary, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem' }}>
              Employés
            </Typography>
            <Typography variant="body1" sx={{ color: theme.colors.primary[600], fontWeight: 800 }}>
              {employeesCount}
            </Typography>
          </MetricBox>
          
          <MetricBox sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: theme.colors.text.tertiary, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem' }}>
              Rendez-vous
            </Typography>
            <Typography variant="body1" sx={{ color: theme.colors.secondary[600], fontWeight: 800 }}>
              {appointmentsCount}
            </Typography>
          </MetricBox>
        </Box>

      </CardContent>
    </StyledCard>
  );
};

export default SalonCard;
